/**
 * Besucher-ID fuer die Zuteilung anonymer LP-Besucher.
 *
 * Persistent (localStorage `aivy_vid`) nur mit CookieScript-Consent fuer
 * `performance`. Ohne Consent lebt die ID nur im Speicher dieses
 * Seitenaufrufs, auf dem Geraet wird nichts abgelegt. Kommt der Consent
 * waehrend des Besuchs, wird die bisherige ID gespeichert, damit die Gruppe
 * gleich bleibt. Wird er abgelehnt oder entzogen, wird sie geloescht.
 *
 * Consent wie in gatsby-browser.js: CookieScript-Events plus
 * CookieScript.instance.currentState(). Solange CookieScript noch nicht geladen
 * ist, gilt das Cookie CookieScriptConsent, sonst bekaeme ein wiederkehrender
 * Besucher mit Consent bei jedem Aufruf eine neue ID.
 */
import { isBrowser } from '../utils'
import {
  CONSENT_CATEGORY,
  hasPerformanceConsentIn,
  isValidVisitorId,
  parseConsentCookie,
  uuidFromBytes
} from './helpers.js'

const STORAGE_KEY = 'aivy_vid'
const CONSENT_COOKIE = 'CookieScriptConsent'

let memoryId = null
// Von CookieScript-Events waehrend des Besuchs gesetzt, null = noch unbekannt
let consentFromEvents = null
let watching = false

const readCookie = (name) => {
  try {
    const prefix = `${name}=`
    const entry = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix))
    return entry ? entry.slice(prefix.length) : null
  } catch {
    return null
  }
}

const readStored = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

const persist = (id) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // localStorage gesperrt (Safari privat o. ae.), ID gilt dann nur im Speicher
  }
}

const forget = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // nichts zu tun
  }
}

export const hasPerformanceConsent = () => {
  if (!isBrowser) return false
  if (consentFromEvents !== null) return consentFromEvents

  try {
    const state = window.CookieScript?.instance?.currentState?.()
    if (state && typeof state.action === 'string') {
      return hasPerformanceConsentIn(state)
    }
  } catch {
    // CookieScript noch nicht bereit, Cookie pruefen
  }

  return hasPerformanceConsentIn(parseConsentCookie(readCookie(CONSENT_COOKIE)))
}

export const createVisitorId = () => {
  const cryptoApi = isBrowser ? window.crypto : undefined
  try {
    // randomUUID gibt es nur in Secure Contexts und neueren Browsern
    if (cryptoApi?.randomUUID) return cryptoApi.randomUUID()
  } catch {
    // Fallback unten
  }

  const bytes = new Uint8Array(16)
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }
  return uuidFromBytes(bytes)
}

export const getVisitorId = () => {
  if (!isBrowser) return null
  if (memoryId) return memoryId

  const consent = hasPerformanceConsent()
  const stored = consent ? readStored() : null

  memoryId = isValidVisitorId(stored) ? stored : createVisitorId()
  if (consent && stored !== memoryId) persist(memoryId)

  return memoryId
}

const applyConsent = (consent) => {
  consentFromEvents = consent
  if (!consent) {
    forget()
    return
  }
  if (memoryId && readStored() !== memoryId) persist(memoryId)
}

/** Einmal pro Seitenaufruf, idempotent. */
export const watchConsent = () => {
  if (!isBrowser || watching) return
  watching = true

  window.addEventListener('CookieScriptAcceptAll', () => applyConsent(true))

  window.addEventListener('CookieScriptAccept', (e) => {
    const categories = e.detail?.categories || []
    // Ohne Kategorien sagt das Event nichts Eindeutiges, „Alle akzeptieren"
    // kommt zusaetzlich als CookieScriptAcceptAll.
    if (categories.length === 0) return
    applyConsent(categories.includes(CONSENT_CATEGORY))
  })

  window.addEventListener('CookieScriptReject', () => applyConsent(false))

  window.addEventListener('CookieScriptLoaded', () => {
    try {
      // Ohne lesbaren State nichts aendern. Ein State ohne Entscheidung heisst
      // dagegen „kein Consent".
      const state = window.CookieScript?.instance?.currentState?.()
      if (state) applyConsent(hasPerformanceConsentIn(state))
    } catch {
      // State nicht lesbar, beim bisherigen Stand bleiben
    }
  })
}
